import { cloneDeep } from 'lodash';
import { formatISO, parseISO } from 'date-fns';
import Client from '../client/client';
import ClientUser from '../client/client-user';
import TechspecDto from './techspec-dto';
import TechspecType from './techspec-type';
import User from '@vertical-plus/vue-js-micro-service-components/lib/model/entity/organisation/user';

export default class Techspec
{
    public dto!: TechspecDto;

    public constructor(dto: TechspecDto)
    {
        this.dto = Object.assign({}, dto);

        this._clientId = this.dto.client ? this.dto.client.id : null;
        this._clientUserId = this.dto.clientUser ? this.dto.clientUser.id : null;
        this._managerId = this.dto.manager ? this.dto.manager.id : null;
        this._typeId = this.dto.type ? this.dto.type.id : null;
    }

    public get id(): number|null
    {
        return this.dto.id;
    }

    public set id(id: number|null)
    {
        this.dto.id = id;
    }

    public get client(): Client|null
    {
        if (!this.dto.client)
        {
            return null;
        }

        return new Client(this.dto.client);
    }

    private _clientId: number|null = null;

    public get clientId(): number|null
    {
        return this._clientId;
    }

    public set clientId(id: number|null)
    {
        this._clientId = id;
    }

    public get clientUser(): ClientUser|null
    {
        if (!this.dto.clientUser)
        {
            return null;
        }

        return new ClientUser(this.dto.clientUser);
    }

    private _clientUserId: number|null = null;

    public get clientUserId(): number|null
    {
        return this._clientUserId;
    }

    public set clientUserId(id: number|null)
    {
        this._clientUserId = id;
    }

    public get manager(): User|null
    {
        if (!this.dto.manager)
        {
            return null;
        }

        return new User(this.dto.manager);
    }

    private _managerId: string|null = null;

    public get managerId(): string|null
    {
        return this._managerId;
    }

    public set managerId(id: string|null)
    {
        this._managerId = id;
    }

    public get type(): TechspecType|null
    {
        if (!this.dto.type)
        {
            return null;
        }

        return new TechspecType(this.dto.type);
    }

    private _typeId: number|null = null;

    public get typeId(): number|null
    {
        return this._typeId;
    }

    public set typeId(id: number|null)
    {
        this._typeId = id;
    }

    public get name(): string
    {
        return this.dto.name;
    }

    public set name(name: string)
    {
        this.dto.name = name;
    }

    public get description(): string|null
    {
        return this.dto.description;
    }

    public set description(description: string|null)
    {
        this.dto.description = description;
    }

    public get meetingDate(): Date|null
    {
        if (!this.dto.meetingDate)
        {
            return null;
        }

        return parseISO(this.dto.meetingDate);
    }

    public set meetingDate(meetingDate: Date|null)
    {
        if (!meetingDate)
        {
            this.dto.meetingDate = null;
            return;
        }

        this.dto.meetingDate = formatISO(meetingDate);
    }

    public get meetingLocation(): string|null
    {
        return this.dto.meetingLocation;
    }

    public set meetingLocation(meetingLocation: string|null)
    {
        this.dto.meetingLocation = meetingLocation;
    }

    public get deadline(): Date|null
    {
        if (!this.dto.deadline)
        {
            return null;
        }

        return parseISO(this.dto.deadline);
    }

    public set deadline(deadline: Date|null)
    {
        if (!deadline)
        {
            this.dto.deadline = null;
            return;
        }

        this.dto.deadline = formatISO(deadline);
    }

    public get project(): boolean
    {
        return this.dto.project;
    }

    public set project(project: boolean)
    {
        this.dto.project = project;
    }

    public get active(): boolean
    {
        return this.dto.active;
    }

    public set active(active: boolean)
    {
        this.dto.active = active;
    }

    public get dateCreated(): Date
    {
        return parseISO(this.dto.dateCreated);
    }

    public set dateCreated(dateCreated: Date)
    {
        this.dto.dateCreated = formatISO(dateCreated);
    }

    public get dateUpdated(): Date
    {
        return parseISO(this.dto.dateUpdated);
    }

    public set dateUpdated(dateUpdated: Date)
    {
        this.dto.dateUpdated = formatISO(dateUpdated);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public get params(): {[key: string]: any}
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dto: any = cloneDeep(this.dto);

        dto.client = this.clientId ? `/clients/${ this.clientId }` : null;
        dto.manager = this.managerId ? `/users/${ this.managerId }` : null;
        dto.type = this.typeId ? `/techspec_types/${ this.typeId }` : null;
        dto.clientUser = this.clientUserId ? `/client_users/${ this.clientUserId }` : null;

        return dto;
    }
}